import Bowser from 'bowser'

export interface UserAgentInfo {
  browser: string
  os: string
  device: string
  userAgent: string
}

interface NavigatorUADataWithHighEntropy {
  platform?: string
  getHighEntropyValues?: (hints: string[]) => Promise<{ platformVersion?: string }>
}

let cachedPlatformVersion: string | undefined
let clientHintsWarmPromise: Promise<void> | undefined

function joinNameVersion (name?: string, version?: string): string {
  return [name, version].filter(Boolean).join(' ')
}

function getClientHints (): Bowser.ClientHints | undefined {
  const nav = globalThis.navigator
  if (!nav || !('userAgentData' in nav)) {
    return undefined
  }
  return (nav as Navigator & { userAgentData?: Bowser.ClientHints }).userAgentData
}

const SAFARI_MARKETING_VERSION_RE = /Version\/(\d+(?:[._]\d+)*)/i

function getSafariMarketingVersion (ua: string): string | undefined {
  const match = ua.match(SAFARI_MARKETING_VERSION_RE)
  return match?.[1]?.replace(/_/g, '.')
}

/**
 * iOS 26+ freezes the CPU iPhone OS token at 18.x while Version/ reflects the
 * real Safari (and iOS) version. Prefer Version/ when it is newer than the
 * parsed OS token on Safari.
 * @see https://docs.uaparser.dev/guides/how-to-detect-ios-26-using-javascript.html
 */
function resolveIosVersion (ua: string, osVersion?: string, browserName?: string): string | undefined {
  if (browserName !== 'Safari' || !osVersion) {
    return osVersion
  }

  const marketingVersion = getSafariMarketingVersion(ua)
  if (!marketingVersion) {
    return osVersion
  }

  const osMajor = parseInt(osVersion.split(/[._]/)[0], 10)
  const marketingMajor = parseInt(marketingVersion.split(/[._]/)[0], 10)
  if (!Number.isNaN(osMajor) && !Number.isNaN(marketingMajor) && marketingMajor > osMajor) {
    return marketingVersion
  }

  return osVersion
}

/**
 * Windows 11+ reports as NT 10.0 in the UA string; platformVersion major >= 13
 * from Client Hints identifies Windows 11 and later.
 * @see https://learn.microsoft.com/en-us/microsoft-edge/web-platform/how-to-detect-win11
 */
function formatOs (ua: string, osName?: string, osVersion?: string, browserName?: string): string {
  if (osName === 'iOS') {
    return joinNameVersion(osName, resolveIosVersion(ua, osVersion, browserName))
  }

  if (osName !== 'Windows') {
    return joinNameVersion(osName, osVersion)
  }

  if (cachedPlatformVersion) {
    const major = parseInt(cachedPlatformVersion.split('.')[0], 10)
    if (!Number.isNaN(major)) {
      if (major >= 13) {
        return joinNameVersion(osName, '11')
      }
      if (major > 0) {
        return joinNameVersion(osName, '10')
      }
    }
  }

  return joinNameVersion(osName, osVersion)
}

/**
 * Loads high-entropy Client Hints (platformVersion) once and caches the result.
 * Safe to call multiple times; subsequent calls share the same promise.
 */
export function warmUserAgentClientHints (): Promise<void> {
  if (!clientHintsWarmPromise) {
    clientHintsWarmPromise = loadPlatformVersion().catch(() => undefined)
  }
  return clientHintsWarmPromise
}

async function loadPlatformVersion (): Promise<void> {
  const nav = globalThis.navigator
  if (!nav || !('userAgentData' in nav)) {
    return
  }

  const userAgentData = (nav as Navigator & { userAgentData?: NavigatorUADataWithHighEntropy }).userAgentData
  if (!userAgentData?.getHighEntropyValues) {
    return
  }

  const { platformVersion } = await userAgentData.getHighEntropyValues(['platformVersion'])
  cachedPlatformVersion = platformVersion
}

/**
 * Parses the current user agent via Bowser (browser, OS, platform).
 */
export function getUserAgentInfo (): UserAgentInfo {
  const ua = globalThis.navigator?.userAgent ?? ''
  const parsed = Bowser.parse(ua, getClientHints())
  const platformType = parsed.platform?.type

  return {
    browser: joinNameVersion(parsed.browser?.name, parsed.browser?.version),
    os: formatOs(ua, parsed.os?.name, parsed.os?.version, parsed.browser?.name),
    device: platformType && platformType !== 'desktop' ? platformType : 'Desktop',
    userAgent: ua
  }
}
